import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Spinner } from '@nextui-org/react';
import { ANALYST_RUNTIMES, analystRoute, useAnalystInsight, type AnalystWorkerLike } from './analystRuntime';

/**
 * An analyst's Dashboard tab. A decision analyst switched to the Insight runtime is asked in the
 * Insight conversation, with its linked agent, so the tab opens that instead of `children` (the
 * NL analyst's panel). Where the Insight runtime is not configured, `children` always shows.
 */
export function AnalystDashboardRoute({ worker, isDecision, isLoadingWorker, children }: {
  readonly worker: AnalystWorkerLike | null | undefined;
  /** The URL says decision analyst: wait for the aiworker before showing the NL analyst's panel. */
  readonly isDecision: boolean;
  readonly isLoadingWorker: boolean;
  readonly children: ReactElement;
}) {
  const { t } = useTranslation();
  const insight = useAnalystInsight();
  if (!insight) return children;
  // Without this wait a question typed before the aiworker loads would still go to the NL analyst.
  if (!worker && isDecision && isLoadingWorker) {
    return <Spinner size="sm" className="self-start" aria-label={t('Loading the analyst')} />;
  }
  const route = analystRoute(worker);
  if (route.runtime === ANALYST_RUNTIMES.INSIGHT) {
    return <Navigate to={insight.conversationUrl(route.agentId)} replace />;
  }
  return children;
}

export default AnalystDashboardRoute;
